type NumericDigit = 'numeric' | '2-digit';

type Options = Intl.DateTimeFormatOptions & {
  // Augmented Intl.DateTimeFormatOptions types
  day?: NumericDigit;
  formatToParts?: boolean;
  hour?: NumericDigit;
  locale?: string;
  minute?: NumericDigit;
  month?: NumericDigit | 'long' | 'short' | 'narrow';
  second?: NumericDigit;
  weekday?: 'long' | 'short' | 'narrow';
  year?: NumericDigit;
};

const defaultOptions: Options = {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  locale: 'en-GB',
};

const getDate = (date: string | Date) => (typeof date === 'string' ? new Date(date) : date);

const getDateFormatter = (opts?: Options) => {
  const {
    formatToParts: formatAsParts,
    locale,
    ...dateTimeFormatOptions
  } = {
    ...defaultOptions,
    ...opts,
  };
  return Intl.DateTimeFormat(locale, dateTimeFormatOptions);
};

export interface DatePartsMap {
  day: Intl.DateTimeFormatPart;
  month: Intl.DateTimeFormatPart;
  weekday?: Intl.DateTimeFormatPart;
  year: Intl.DateTimeFormatPart;
}

const getDatePartsMap = (parts: Intl.DateTimeFormatPart[]) =>
  parts.reduce((map, part) => {
    /*
      Exclude 'literal' type object(s), such as ones containing spaces, commas,
      and other separators.
    */
    if (part.type === 'literal') {
      return map;
    }

    return {
      ...map,
      [part.type]: part,
    };
  }, {} as DatePartsMap);

export const formatDate = (dateValue: string | Date, options?: Options) => {
  const formatter = getDateFormatter(options);
  const date = getDate(dateValue);

  if (options?.formatToParts) {
    const parts = formatter.formatToParts(date);

    return getDatePartsMap(parts);
  }

  return formatter.format(date);
};
